import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import {
  List,
  X,
  ArrowRight,
  SignIn,
  UserPlus,
  EnvelopeSimple,
  Phone as PhoneIcon,
  YoutubeLogo,
  FacebookLogo,
  InstagramLogo,
  TelegramLogo,
  XLogo,
} from "@phosphor-icons/react";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "../ui/sheet";
import { COMPANY, NAV_LINKS, APP_URLS, SOCIAL_LINKS } from "../../data/site";

const socialIconMap = {
  YoutubeLogo,
  FacebookLogo,
  InstagramLogo,
  TelegramLogo,
  XLogo,
};

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close sidebar on route change
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-surface/85 backdrop-blur-xl border-b border-line/70 shadow-[0_2px_24px_-12px_rgba(23,43,69,0.18)]"
          : "bg-surface/40 backdrop-blur-md border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-10">
        <div className="flex items-center justify-between h-[72px] gap-4">
          {/* Brand */}
          <Link
            to="/"
            data-testid="header-logo-link"
            className="flex items-center group flex-shrink-0"
          >
            <img
              src={COMPANY.logo}
              alt={COMPANY.name}
              className="h-14 w-auto object-contain"
            />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden xl:flex items-center gap-1 bg-surfaceAlt/70 backdrop-blur-md border border-line rounded-full px-2 py-1.5 shadow-sm">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === "/"}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `px-3.5 py-2 text-sm font-medium rounded-full transition-all duration-200 ${
                    isActive
                      ? "bg-brandNavy text-surfaceAlt shadow-sm"
                      : "text-ink700 hover:text-ink900 hover:bg-line/60"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
          </nav>

          {/* CTAs (desktop) */}
          <div className="hidden lg:flex items-center gap-2">
            <a
              href={APP_URLS.login}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-login-link"
              className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full text-sm font-semibold text-ink900 hover:bg-line/50 transition-colors"
            >
              <SignIn size={16} weight="bold" />
              Login
            </a>
            <a
              href={APP_URLS.register}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-register-cta"
              className="btn-shimmer group inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brandNavy text-surfaceAlt text-sm font-semibold hover:bg-brandBlue transition-all duration-200 brand-glow"
            >
              Register
              <ArrowRight
                size={16}
                weight="bold"
                className="transition-transform group-hover:translate-x-0.5"
              />
            </a>
          </div>

          {/* Mobile / tablet menu trigger */}
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                type="button"
                data-testid="mobile-menu-toggle"
                aria-label="Open menu"
                className="xl:hidden h-11 w-11 rounded-full border border-line bg-surfaceAlt flex items-center justify-center text-ink900 hover:bg-line/40 flex-shrink-0"
              >
                <List size={20} weight="bold" />
              </button>
            </SheetTrigger>

            <SheetContent
              side="right"
              data-testid="mobile-sidebar"
              className="w-[85vw] sm:max-w-md bg-surface border-l border-line p-0 flex flex-col [&>button.absolute]:hidden"
            >
              {/* Sidebar header */}
              <div className="flex items-center justify-between px-6 py-5 border-b border-line">
                <img
                  src={COMPANY.logo}
                  alt={COMPANY.name}
                  className="h-12 w-auto object-contain"
                />
                <button
                  type="button"
                  data-testid="mobile-sidebar-close"
                  onClick={() => setOpen(false)}
                  aria-label="Close menu"
                  className="h-10 w-10 rounded-full border border-line bg-surfaceAlt flex items-center justify-center text-ink900 hover:bg-line/40"
                >
                  <X size={18} weight="bold" />
                </button>
              </div>

              {/* Nav list */}
              <nav className="flex-1 overflow-y-auto px-4 py-5">
                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-ink500 px-3 mb-2">
                  Menu
                </div>
                <ul className="space-y-1">
                  {NAV_LINKS.map((link) => (
                    <li key={link.to}>
                      <NavLink
                        to={link.to}
                        end={link.to === "/"}
                        data-testid={`sidebar-nav-link-${link.label.toLowerCase()}`}
                        className={({ isActive }) =>
                          `group flex items-center justify-between px-4 py-3.5 rounded-2xl text-sm font-semibold transition-all ${
                            isActive
                              ? "bg-brandNavy text-surfaceAlt"
                              : "text-ink900 hover:bg-line/50"
                          }`
                        }
                      >
                        {({ isActive }) => (
                          <>
                            <span>{link.label}</span>
                            <ArrowRight
                              size={14}
                              weight="bold"
                              className={`transition-all ${
                                isActive
                                  ? "translate-x-0 opacity-100"
                                  : "-translate-x-1 opacity-0 group-hover:opacity-70 group-hover:translate-x-0"
                              }`}
                            />
                          </>
                        )}
                      </NavLink>
                    </li>
                  ))}
                </ul>

                {/* Contact strip */}
                <div className="mt-7 mx-1 px-4 py-4 rounded-2xl bg-surfaceAlt border border-line">
                  <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-ink500 mb-2">
                    Get in touch
                  </div>
                  <a
                    href={`mailto:${COMPANY.email}`}
                    data-testid="sidebar-email"
                    className="flex items-center gap-2 text-sm font-semibold text-ink900 mb-1.5 hover:text-infoText"
                  >
                    <EnvelopeSimple size={14} weight="duotone" className="text-infoText" />
                    {COMPANY.email}
                  </a>
                  <a
                    href={`tel:${COMPANY.phone.replace(/\s/g, "")}`}
                    data-testid="sidebar-phone"
                    className="flex items-center gap-2 text-sm font-semibold text-ink900 hover:text-infoText"
                  >
                    <PhoneIcon size={14} weight="duotone" className="text-infoText" />
                    {COMPANY.phone}
                  </a>
                </div>

                {/* Social */}
                <div className="mt-5 flex items-center justify-center gap-2.5">
                  {SOCIAL_LINKS.map(({ label, url, icon }) => {
                    const Icon = socialIconMap[icon] || XLogo;
                    return (
                      <a
                        key={label}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`sidebar-social-${label.toLowerCase()}`}
                        aria-label={label}
                        className="h-9 w-9 rounded-full border border-line bg-surfaceAlt flex items-center justify-center text-ink700 hover:bg-brandNavy hover:text-white hover:border-brandNavy transition-all"
                      >
                        <Icon size={15} weight="bold" />
                      </a>
                    );
                  })}
                </div>
              </nav>

              {/* CTA footer */}
              <div className="px-4 py-4 border-t border-line bg-surfaceAlt/70 backdrop-blur space-y-2.5">
                <Link
                  to="/become-partner"
                  data-testid="sidebar-become-partner-cta"
                  className="btn-shimmer flex items-center justify-center gap-2 px-5 py-3.5 rounded-full bg-brandOrange text-ink900 text-sm font-bold hover:bg-brandBlue hover:text-surfaceAlt transition-colors"
                >
                  Become Partner
                  <ArrowRight size={15} weight="bold" />
                </Link>
                <div className="grid grid-cols-2 gap-2.5">
                  <a
                    href={APP_URLS.login}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="sidebar-login-cta"
                    className="inline-flex items-center justify-center gap-1.5 px-4 py-3 rounded-full border border-line text-ink900 text-sm font-bold hover:bg-line/40"
                  >
                    <SignIn size={15} weight="bold" />
                    Login
                  </a>
                  <a
                    href={APP_URLS.register}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="sidebar-register-cta"
                    className="btn-shimmer inline-flex items-center justify-center gap-1.5 px-4 py-3 rounded-full bg-brandNavy text-surfaceAlt text-sm font-bold hover:bg-brandBlue"
                  >
                    <UserPlus size={15} weight="bold" />
                    Register
                  </a>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
